/**
 * Titans "Learning by Surprise" — Type Definitions
 *
 * Based on Google Research papers:
 * - Titans: Learning to Memorize at Test Time (2501.00663)
 * - Learning by Surprise: Surplexity for Mitigating Model Collapse (2410.12341)
 */

/** Breakdown of surprise factors scored 0–100 */
export interface SurpriseFactors {
    novelty: number;        // How different from past training events
    complexity: number;     // Decision difficulty & text complexity
    contextBreak: number;   // How much it violates expected patterns
    edgeCaseScore: number;  // Likelihood of being an unusual edge case
}

/** Full surprise assessment for a training case */
export interface SurpriseMetrics {
    /** Overall surprise score (0–100, higher = more valuable for learning) */
    score: number;
    /** Confidence in the calculation (0–1) */
    confidence: number;
    /** Factor breakdown */
    factors: SurpriseFactors;
    /** Human-readable explanation lines */
    explanation: string[];
}

/** Tunable configuration for the surprise engine */
export interface SurpriseConfig {
    /** Minimum surprise to consider "surprising" */
    surpriseThreshold: number;
    /** Weight factors for each component */
    weights: SurpriseFactors;
    /** 0–1, how much to favor surprising cases vs random */
    surpriseBias: number;
    /** Master toggle */
    enabled: boolean;
}

export const DEFAULT_SURPRISE_CONFIG: SurpriseConfig = {
    surpriseThreshold: 60,
    weights: {
        novelty: 0.35,
        complexity: 0.25,
        contextBreak: 0.25,
        edgeCaseScore: 0.15,
    },
    surpriseBias: 0.7,
    enabled: true,
};
